<template>
  <div
    id="plan-section"
    class="i-scoping-details"
  >
    <div class="left">
      <div class="details">
        <div>{{ props.title }}</div>
        <div>
          <div class="price">
            <span
              v-if="props.discounted"
              class="discounted"
            >
              ${{ props.discounted }}
            </span>
            <span>${{ props.price }}</span>
          </div>
          <div
            v-if="props.off"
            class="off"
          >
            {{ props.off }}{{ props.countdownTimer ? ` off for the next ${props.countdownTimer}` : " Off" }}
          </div>
        </div>
      </div>
      <div class="included">
        <div>What's Included:</div>
        <div class="list">
          <div class="item">
            <img :src="iconCheck" />
            <span>Career Report</span>
          </div>
          <div class="row">
            <div class="item">
              <img :src="iconCheck" />
              <span>Discovery Coaching Session</span>
            </div>
            <div class="item">
              <img :src="iconClock" />
              <span>30 minutes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="perks">
      <template
        v-for="perk in PERKS"
        :key="perk.icon"
      >
        <div class="perk">
          <img :src="perk.icon" />
          <div class="text">
            <div class="main">{{ perk.title }}</div>
            {{ perk.text }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
// ASSETS
import iconCheck from "@/assets/icons/icon-check-bright-green.svg"
import iconIconUnstuck from "@/assets/icons/icon-scoping-unstuck.svg"
import iconClock from "@/assets/icons/icon-clock.svg"
import iconDirection from "@/assets/icons/icon-scoping-direction.svg"
import iconRoadmap from "@/assets/icons/icon-scoping-roadmap.svg"
import iconStrengths from "@/assets/icons/icon-scoping-strengths.svg"

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  price: {
    type: String,
    required: true
  },
  discounted: {
    type: String,
    default: null
  },
  off: {
    type: String,
    default: null
  },
  countdownTimer: {
    type: String,
    default: null
  }
})

// CONSTANTS
const PERKS = [
  {
    icon: iconDirection,
    title: "Find Your Direction",
    text: "Stop second-guessing and focus your search where it counts most"
  },
  { icon: iconRoadmap, title: "Get Your Roadmap", text: "Walk away with a clear, actionable plan that gets results" },
  {
    icon: iconStrengths,
    title: "Leverage Your Strengths",
    text: "Turn assessment results into your competitive advantage"
  },
  { icon: iconIconUnstuck, title: "Get Unstuck", text: "Replace confusion with confidence and clear next steps" }
]
</script>

<style lang="scss" scoped>
.i-scoping-details {
  @include flex(column, start, stretch, $gap: 25px);
  @include border-radius-md;

  margin: 40px 15px 50px;
  background-color: $white;
  box-shadow:
    0 10px 15px -3px #0000001a,
    0 4px 6px -4px #0000001a;
  padding: 28px;

  @media screen and (min-width: $breakpoint-md) {
    width: max-content;
    margin: 50px auto;
  }

  @media screen and (min-width: $breakpoint-lg) {
    @include flex(row, center, center, $gap: 35px);
  }

  @media screen and (min-width: $breakpoint-xl) {
    gap: 55px;
  }

  .left {
    @include flex(column, start, start, $gap: 20px);

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, space-around, center, $gap: 20px);
    }

    @media screen and (min-width: $breakpoint-lg) {
      @include flex(column, start, start, $gap: 20px);
    }

    .details {
      @include flex(column, start, start, $gap: 3px);
      @include text-lg;

      font-weight: 600;

      .price {
        @include flex(row, start, center, $gap: 6px);

        .discounted {
          font-weight: 400;
          text-decoration: line-through;
          color: $comet400;
        }
      }

      .off {
        @include text-xs;

        font-weight: 700;
        color: $bittersweet400;
      }
    }

    .included {
      @include flex(column, start, start, $gap: 8px);
      @include text-sm;

      font-weight: 600;

      .list {
        @include flex(column, start, start, $gap: 8px);

        font-weight: 400;

        .row {
          @include flex(column, start, start, $gap: 8px);

          @media screen and (min-width: $breakpoint-md) {
            @include flex(row, start, center, $gap: 25px);
          }
        }

        .item {
          @include flex(row, start, center, $gap: 6px);

          color: $comet700;
        }
      }
    }
  }

  .perks {
    @include grid($cols: 1fr, $rows: auto, $align-items: center, $gap: 16px);

    @media screen and (min-width: $breakpoint-md) {
      @include grid($cols: repeat(2, 280px), $rows: repeat(2, 1fr), $align-items: stretch, $gap: 20px);
    }

    .perk {
      @include flex(row, start, start, $gap: 14px);
      @include text-sm;

      .text {
        @include flex(column, start, start, $gap: 2px);

        color: $comet700;

        .main {
          @include text-md;

          font-weight: 600;
          color: $black950;
        }
      }
    }
  }
}
</style>
